import dagre from 'dagre';
import type { NodePosition } from '@mct/shared';

export interface LayoutNodeInput {
  id: string;
  width: number;
  height: number;
}

export interface LayoutEdgeInput {
  source: string;
  target: string;
}

/**
 * Run dagre auto-layout over a task graph and return `id -> position` map.
 * Edges are drawn from parent (dependsOn) to child (so roots appear at top).
 *
 * Direction TB (top -> bottom) matches the spec's figure in §7.2.
 */
export function computeDagLayout(
  nodes: readonly LayoutNodeInput[],
  edges: readonly LayoutEdgeInput[],
  options: { direction?: 'TB' | 'LR'; ranksep?: number; nodesep?: number } = {},
): Record<string, NodePosition> {
  const g = new dagre.graphlib.Graph({ directed: true });
  g.setGraph({
    rankdir: options.direction ?? 'TB',
    ranksep: options.ranksep ?? 80,
    nodesep: options.nodesep ?? 40,
    marginx: 20,
    marginy: 20,
  });
  g.setDefaultEdgeLabel(() => ({}));

  for (const n of nodes) {
    g.setNode(n.id, { width: n.width, height: n.height });
  }
  for (const e of edges) {
    g.setEdge(e.source, e.target);
  }

  dagre.layout(g);

  const result: Record<string, NodePosition> = {};
  for (const n of nodes) {
    const laid = g.node(n.id);
    if (!laid) continue;
    // dagre reports center coords; React Flow expects top-left.
    result[n.id] = {
      x: laid.x - n.width / 2,
      y: laid.y - n.height / 2,
    };
  }
  return result;
}
